import { useState, useEffect } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, User, ArrowRight, Target, Mail, MessageSquare, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamAthletes = () => {
  const { currentTeam, isManager } = useOutletContext();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Normalize a phone string to E.164-ish digits for wa.me deep links.
  // wa.me expects digits only with country code. We strip everything that
  // isn't a digit and trust admin-entered numbers to include the country.
  const waLink = (phone) => {
    if (!phone) return null;
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length < 8) return null; // too short to be a real number
    return `https://wa.me/${digits}`;
  };

  useEffect(() => {
    if (currentTeam) {
      fetchAthletes();
    }
  }, [currentTeam]);

  const fetchAthletes = async () => {
    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/athletes`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
      }
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAthletes = athletes.filter(athlete =>
    athlete.name?.toLowerCase().includes(search.toLowerCase()) ||
    athlete.position?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-athletes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Athletes</h1>
          <p className="text-muted-foreground mt-1">
            {athletes.length} athletes in {currentTeam?.team_name}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search athletes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-athletes"
        />
      </div>

      {/* Athletes Grid */}
      {filteredAthletes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Athletes Found</h3>
            <p className="text-sm text-muted-foreground">
              {search ? 'Try a different search term' : 'No athletes have been added to this team yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAthletes.map((athlete) => {
            const wa = waLink(athlete.parent_phone);
            const hasParentContact = !!(athlete.parent_email || athlete.parent_phone);
            return (
              <Card
                key={athlete.id}
                className="hover:border-primary/50 transition-colors h-full"
                data-testid={`athlete-card-${athlete.id}`}
              >
                <CardContent className="p-4">
                  {/* Athlete header — clickable region opens detail */}
                  <Link
                    to={`/team/${currentTeam?.team_id}/athletes/${athlete.id}`}
                    className="flex items-center gap-4"
                    data-testid={`athlete-open-${athlete.id}`}
                  >
                    {athlete.photo_url ? (
                      <img
                        src={`${API_URL}${athlete.photo_url}`}
                        alt={athlete.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-primary">
                          {athlete.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{athlete.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {athlete.age && <span>{athlete.age} years</span>}
                        {athlete.position && (
                          <>
                            <span>•</span>
                            <span className="truncate">{athlete.position}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <ArrowRight size={18} className="text-muted-foreground" />
                  </Link>

                  {/* Parent contact line */}
                  {athlete.parent_name && (
                    <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                      <span className="block truncate">
                        <span className="text-foreground">{athlete.parent_name}</span>
                        {athlete.parent_email && (
                          <span className="ml-1 opacity-80">· {athlete.parent_email}</span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Multi-channel contact buttons — pick the most appropriate channel */}
                  {hasParentContact && (
                    <div className="mt-3 flex items-center gap-1.5" data-testid={`athlete-contact-${athlete.id}`}>
                      {athlete.parent_email && (
                        <a
                          href={`mailto:${athlete.parent_email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-muted/60 hover:bg-primary/10 hover:text-primary text-xs transition"
                          title={`Email ${athlete.parent_name || 'parent'}`}
                          data-testid={`athlete-email-${athlete.id}`}
                        >
                          <Mail size={12} /> Email
                        </a>
                      )}
                      {wa && (
                        <a
                          href={wa}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-muted/60 hover:bg-emerald-500/10 hover:text-emerald-400 text-xs transition"
                          title={`WhatsApp ${athlete.parent_phone}`}
                          data-testid={`athlete-whatsapp-${athlete.id}`}
                        >
                          <Phone size={12} /> WhatsApp
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/team/${currentTeam?.team_id}/messages?athlete_id=${athlete.id}`);
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-muted/60 hover:bg-primary/10 hover:text-primary text-xs transition"
                        title="Open in-app chat"
                        data-testid={`athlete-chat-${athlete.id}`}
                      >
                        <MessageSquare size={12} /> Chat
                      </button>
                    </div>
                  )}

                  {/* Footer dev label */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Target size={12} />
                        View Development
                      </span>
                      <span>Soccer</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Manager Note */}
      {isManager && (
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="py-4">
            <p className="text-sm text-blue-400">
              <strong>Team Manager view:</strong> Use the contact buttons on each athlete card
              to reach parents via Email, WhatsApp or the in-app Chat — pick the channel that
              works best for each family.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeamAthletes;
